import React, { useRef, useEffect } from 'react';
import { useTheme } from '../hooks/useTheme';
import { useCloudSync } from '../hooks/useCloudSync';

const FONT_LABELS = {
  uthmani: 'Uthmani',
  indopak: 'IndoPak',
  tajweed: 'Tajweed',
};

const getFontFamily = (fontType) => {
  switch (fontType) {
    case 'indopak': return "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif";
    case 'tajweed': return "'Scheherazade New', 'Amiri', serif";
    default: return "'Amiri', 'Traditional Arabic', serif";
  }
};

/**
 * Modern Settings Drawer inspired by Quran.com
 * Professional, organized configuration panel with visual sections
 */
const SettingsDrawer = ({
  isOpen,
  onClose,
  fontSize,
  setFontSize,
  fontType,
  setFontType,
  lineSpacing,
  setLineSpacing,
  showUrdu,
  setShowUrdu,
  showEnglish,
  setShowEnglish,
  readingMode,
  setReadingMode,
}) => {
  const { theme, setThemeMode } = useTheme();
  const {
    isSynced,
    isLoading,
    error,
    lastSynced,
    clearError,
    enableSync,
    loginSync,
    disableSync,
    manualSync,
  } = useCloudSync();

  // Derive translationLang from the actual source of truth (showUrdu/showEnglish)
  const translationLang = showUrdu && showEnglish ? 'en-urdu' : showEnglish ? 'en' : 'urdu';

  // Auto-dismiss error toast after 5 seconds
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(clearError, 5000);
    return () => clearTimeout(t);
  }, [error, clearError]);

  // Format last-synced timestamp
  const formatLastSynced = (date) => {
    if (!date) return null;
    const now = Date.now();
    const diff = now - date.getTime();
    if (diff < 60_000) return 'Just now';
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  // Sample Ayah for preview (Al-Baqarah 2:5)
  const sampleAyah = 'أُوْلَـٰٓئِكَ عَلَىٰ هُدًى مِّن رَّبِّهِمْ ۖ وَأُوْلَـٰٓئِكَ هُمُ ٱلْمُفْلِحُونَ';

  const handleReset = () => {
    if (window.confirm('Reset all settings to default?')) {
      setThemeMode('auto');
      setFontSize(32);
      setFontType('uthmani');
      setLineSpacing('normal');
      setShowUrdu(true);
      setShowEnglish(true);
      setReadingMode('scroll');
      const SETTINGS_KEYS = [
        'theme',
        'fontType',
        'translationLang',
        'readingMode',
        'fontSize',
        'lineSpacing',
      ];
      SETTINGS_KEYS.forEach((key) => localStorage.removeItem(key));
    }
  };

  // ---- Backup & Restore ----
  const fileInputRef = useRef(null);

  const BACKUP_KEYS = [
    'quran_bookmarks',
    'quran_notes',
    'quran_last_read',
    'theme',
    'quran-audio-state',
    'readingMode',
    'fontSize',
    'lineSpacing',
    'fontType',
  ];

  const handleExport = () => {
    const data = {};
    BACKUP_KEYS.forEach((key) => {
      const raw = localStorage.getItem(key);
      if (raw !== null) {
        try { data[key] = JSON.parse(raw); } catch { data[key] = raw; }
      }
    });
    const backup = { version: 1, exportedAt: new Date().toISOString(), data };
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quran-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = ''; // allow re-importing same file
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const backup = JSON.parse(ev.target.result);
        if (!backup.version || !backup.data || typeof backup.data !== 'object') {
          alert('Invalid backup file. Please select a valid Quran backup.');
          return;
        }
        Object.entries(backup.data).forEach(([key, value]) => {
          if (BACKUP_KEYS.includes(key)) {
            localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
          }
        });
        window.location.reload();
      } catch {
        alert('Failed to read backup file. Please ensure it is a valid JSON file.');
      }
    };
    reader.readAsText(file);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity"
        onClick={onClose}
        style={{
          paddingTop: 'var(--safe-area-top)',
          paddingBottom: 'var(--safe-area-bottom)',
        }}
      />

      {/* Drawer */}
      <div
        className="fixed right-0 top-0 bottom-0 w-full md:w-[480px] z-50 overflow-y-auto shadow-2xl"
        style={{
          background: 'var(--bg-primary)',
          paddingTop: 'var(--safe-area-top)',
          paddingBottom: 'var(--safe-area-bottom)',
        }}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 px-6 py-4 border-b backdrop-blur-md"
          style={{
            background: 'var(--bg-overlay)',
            borderColor: 'var(--border-default)',
          }}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold" style={{ color: 'var(--text-english)' }}>
              Reading Settings
            </h2>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-95"
              style={{
                background: 'var(--button-default)',
                color: 'var(--text-secondary)',
              }}
              aria-label="Close settings"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-8">

          {/* Theme Section */}
          <section>
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>
              THEME
            </h3>
            <div
              className="flex gap-2 p-1.5 rounded-full"
              style={{ background: 'rgba(148, 163, 184, 0.08)' }}
            >
              {['auto', 'light', 'sepia', 'dark'].map((t) => (
                <button
                  key={t}
                  onClick={() => setThemeMode(t)}
                  className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium transition-all capitalize active:scale-95"
                  style={{
                    background: theme === t ? 'var(--bg-secondary)' : 'transparent',
                    color: theme === t ? 'var(--text-english)' : 'var(--text-secondary)',
                    boxShadow: theme === t ? 'var(--shadow-sm)' : 'none',
                    fontWeight: theme === t ? '600' : '500',
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
          </section>

          {/* Divider */}
          <div style={{ height: '1px', background: 'var(--border-subtle)' }} />

          {/* Quran Font Section */}
          <section>
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>
              QURAN FONT
            </h3>

            {/* Font Preview Card */}
            <div
              className="p-6 rounded-xl mb-4 border"
              style={{
                background: 'var(--bg-elevated)',
                borderColor: 'var(--border-default)',
                boxShadow: 'var(--shadow-sm)',
              }}
            >
              <div
                className="text-center mb-2"
                style={{
                  fontSize: `${fontSize}px`,
                  lineHeight: '2',
                  color: 'var(--text-arabic)',
                  fontFamily: getFontFamily(fontType),
                  direction: 'rtl',
                }}
              >
                {sampleAyah}
              </div>
              <p className="text-xs text-center" style={{ color: 'var(--text-secondary)' }}>
                Al-Baqarah 2:5 • Preview
              </p>
            </div>

            {/* Font Size Controls */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium" style={{ color: 'var(--text-english)' }}>
                  Font Size
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setFontSize(Math.max(20, fontSize - 2))}
                    className="w-8 h-8 rounded-lg flex items-center justify-center font-bold transition-all active:scale-95"
                    style={{
                      background: 'var(--button-default)',
                      color: 'var(--text-english)',
                      border: '1px solid var(--border-default)',
                    }}
                    disabled={fontSize <= 20}
                  >
                    −
                  </button>
                  <span className="text-sm font-mono font-semibold w-12 text-center" style={{ color: 'var(--text-english)' }}>
                    {fontSize}px
                  </span>
                  <button
                    onClick={() => setFontSize(Math.min(48, fontSize + 2))}
                    className="w-8 h-8 rounded-lg flex items-center justify-center font-bold transition-all active:scale-95"
                    style={{
                      background: 'var(--button-default)',
                      color: 'var(--text-english)',
                      border: '1px solid var(--border-default)',
                    }}
                    disabled={fontSize >= 48}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* Font Type Selection */}
            <div>
              <label className="text-sm font-medium block mb-2" style={{ color: 'var(--text-english)' }}>
                Font Type
              </label>
              <div
                className="flex gap-2 p-1.5 rounded-xl"
                style={{ background: 'rgba(148, 163, 184, 0.08)' }}
              >
                {['uthmani', 'indopak', 'tajweed'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setFontType(type)}
                    className="flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all active:scale-95"
                    style={{
                      background: fontType === type ? 'var(--bg-secondary)' : 'transparent',
                      color: fontType === type ? 'var(--text-english)' : 'var(--text-secondary)',
                      boxShadow: fontType === type ? 'var(--shadow-sm)' : 'none',
                      fontWeight: fontType === type ? '600' : '500',
                    }}
                  >
                    {FONT_LABELS[type]}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Divider */}
          <div style={{ height: '1px', background: 'var(--border-subtle)' }} />

          {/* Reading Mode Section */}
          <section>
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>
              READING MODE
            </h3>
            <div
              className="flex gap-2 p-1.5 rounded-xl"
              style={{ background: 'rgba(148, 163, 184, 0.08)' }}
            >
              <button
                onClick={() => setReadingMode('scroll')}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all active:scale-95"
                style={{
                  background: readingMode === 'scroll' ? 'var(--bg-secondary)' : 'transparent',
                  color: readingMode === 'scroll' ? 'var(--text-english)' : 'var(--text-secondary)',
                  boxShadow: readingMode === 'scroll' ? 'var(--shadow-sm)' : 'none',
                  fontWeight: readingMode === 'scroll' ? '600' : '500',
                }}
              >
                📜 Flow
              </button>
              <button
                onClick={() => setReadingMode('ayah')}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all active:scale-95"
                style={{
                  background: readingMode === 'ayah' ? 'var(--bg-secondary)' : 'transparent',
                  color: readingMode === 'ayah' ? 'var(--text-english)' : 'var(--text-secondary)',
                  boxShadow: readingMode === 'ayah' ? 'var(--shadow-sm)' : 'none',
                  fontWeight: readingMode === 'ayah' ? '600' : '500',
                }}
              >
                🎯 Focus
              </button>
            </div>
          </section>

          {/* Divider */}
          <div style={{ height: '1px', background: 'var(--border-subtle)' }} />

          {/* Translation Section */}
          <section>
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>
              TRANSLATION
            </h3>

            {/* Translation Language Dropdown */}
            <div className="mb-4">
              <label className="text-sm font-medium block mb-2" style={{ color: 'var(--text-english)' }}>
                Language
              </label>
              <select
                value={translationLang}
                onChange={(e) => {
                  const val = e.target.value;
                  setShowUrdu(val === 'urdu' || val === 'en-urdu');
                  setShowEnglish(val === 'en' || val === 'en-urdu');
                }}
                className="w-full px-4 py-2.5 rounded-lg text-sm transition-all"
                style={{
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-default)',
                  color: 'var(--text-english)',
                }}
              >
                <option value="en-urdu">English + Urdu</option>
                <option value="en">English Only (Dr. Mustafa Khattab)</option>
                <option value="urdu">Urdu Only (Maulana Fateh Muhammad Jalandhari)</option>
              </select>
            </div>

            {/* Translation Toggles */}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (showUrdu && !showEnglish) return;
                  setShowUrdu(!showUrdu);
                }}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all active:scale-95"
                style={{
                  background: showUrdu ? 'var(--gold-muted)' : 'var(--button-default)',
                  border: showUrdu ? '2px solid var(--border-gold)' : '2px solid var(--border-default)',
                  color: showUrdu ? 'var(--gold-hover)' : 'var(--text-english)',
                  fontWeight: showUrdu ? '600' : '500',
                  opacity: showUrdu && !showEnglish ? 0.6 : 1,
                  cursor: showUrdu && !showEnglish ? 'not-allowed' : 'pointer',
                }}
              >
                {showUrdu ? '✓ ' : ''}Urdu
              </button>
              <button
                onClick={() => {
                  if (showEnglish && !showUrdu) return;
                  setShowEnglish(!showEnglish);
                }}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all active:scale-95"
                style={{
                  background: showEnglish ? 'var(--gold-muted)' : 'var(--button-default)',
                  border: showEnglish ? '2px solid var(--border-gold)' : '2px solid var(--border-default)',
                  color: showEnglish ? 'var(--gold-hover)' : 'var(--text-english)',
                  fontWeight: showEnglish ? '600' : '500',
                  opacity: showEnglish && !showUrdu ? 0.6 : 1,
                  cursor: showEnglish && !showUrdu ? 'not-allowed' : 'pointer',
                }}
              >
                {showEnglish ? '✓ ' : ''}English
              </button>
            </div>
          </section>

          {/* Divider */}
          <div style={{ height: '1px', background: 'var(--border-subtle)' }} />

          {/* Display Options Section */}
          <section>
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>
              DISPLAY OPTIONS
            </h3>

            {/* Line Spacing */}
            <div>
              <label className="text-sm font-medium block mb-2" style={{ color: 'var(--text-english)' }}>
                Line Spacing
              </label>
              <div
                className="flex gap-2 p-1.5 rounded-xl"
                style={{ background: 'rgba(148, 163, 184, 0.08)' }}
              >
                {['compact', 'normal', 'relaxed'].map((spacing) => (
                  <button
                    key={spacing}
                    onClick={() => setLineSpacing(spacing)}
                    className="flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all capitalize active:scale-95"
                    style={{
                      background: lineSpacing === spacing ? 'var(--bg-secondary)' : 'transparent',
                      color: lineSpacing === spacing ? 'var(--text-english)' : 'var(--text-secondary)',
                      boxShadow: lineSpacing === spacing ? 'var(--shadow-sm)' : 'none',
                      fontWeight: lineSpacing === spacing ? '600' : '500',
                    }}
                  >
                    {spacing}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Error Toast */}
          {error && (
            <div
              className="flex items-start gap-3 p-3 rounded-xl text-sm"
              style={{
                background: 'rgba(239, 68, 68, 0.12)',
                border: '1px solid rgba(239, 68, 68, 0.35)',
                color: 'var(--error, #f87171)',
              }}
            >
              <span className="text-base leading-none mt-0.5">⚠️</span>
              <div className="flex-1">{error}</div>
              <button
                onClick={clearError}
                className="flex-shrink-0 text-xs opacity-60 hover:opacity-100 transition-opacity"
                aria-label="Dismiss error"
              >
                ✕
              </button>
            </div>
          )}

          {/* Divider */}
          <div style={{ height: '1px', background: 'var(--border-subtle)' }} />

          {/* ── Cloud Sync Section ──────────────────────────────────────────── */}
          <section>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>
                CLOUD SYNC
              </h3>
              {/* Status badge */}
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                style={
                  isSynced
                    ? { background: 'rgba(34, 197, 94, 0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)' }
                    : { background: 'rgba(148, 163, 184, 0.1)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }
                }
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: isSynced ? '#4ade80' : 'var(--text-secondary)' }}
                />
                {isSynced ? 'Sync Active' : 'Not Synced'}
              </span>
            </div>

            <p className="text-xs mb-4" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
              {isSynced
                ? 'Your bookmarks, notes, and last read position sync across all your devices automatically.'
                : 'Link your devices with a fingerprint or Face ID — no password needed.'}
            </p>

            {/* ── Authenticated state ───────────────────────────────────────── */}
            {isSynced && (
              <div className="space-y-3">
                {/* Last synced row */}
                <div
                  className="flex items-center justify-between px-4 py-3 rounded-xl"
                  style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
                >
                  <div>
                    <p className="text-xs font-medium" style={{ color: 'var(--text-english)' }}>
                      Last Synced
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
                      {lastSynced ? formatLastSynced(lastSynced) : 'Never'}
                    </p>
                  </div>
                  {/* Manual sync button */}
                  <button
                    onClick={manualSync}
                    disabled={isLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all active:scale-95 disabled:opacity-50"
                    style={{
                      background: 'var(--gold-muted)',
                      border: '1px solid var(--border-gold)',
                      color: 'var(--gold-hover)',
                    }}
                    aria-label="Sync now"
                  >
                    {isLoading ? (
                      <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    )}
                    {isLoading ? 'Syncing…' : 'Sync Now'}
                  </button>
                </div>

                {/* Sign out */}
                <button
                  onClick={() => {
                    if (window.confirm('Sign out and disable cloud sync on this device?')) {
                      disableSync();
                    }
                  }}
                  className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-[0.98]"
                  style={{
                    background: 'var(--button-default)',
                    border: '2px solid var(--border-strong)',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Sign Out / Disable Sync
                </button>
              </div>
            )}

            {/* ── Unauthenticated state ─────────────────────────────────────── */}
            {!isSynced && (
              <div className="space-y-3">
                {/* Primary: Enable (registration — first device) */}
                <button
                  onClick={enableSync}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    background: isLoading ? 'var(--gold-muted)' : 'linear-gradient(135deg, var(--gold-primary), var(--gold-hover))',
                    border: '2px solid var(--border-gold)',
                    color: 'white',
                    boxShadow: isLoading ? 'none' : '0 4px 14px var(--gold-glow)',
                  }}
                >
                  {isLoading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Waiting for biometric…
                    </>
                  ) : (
                    <>
                      {/* Fingerprint icon */}
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M12 11c0-1.1-.9-2-2-2s-2 .9-2 2v2a2 2 0 002 2h0a2 2 0 002-2v-2zm0 0V9a4 4 0 00-8 0v4m8-2a4 4 0 018 0v4m-8-2c0 3.3 2.7 6 6 6m-6 0c-3.3 0-6-2.7-6-6" />
                      </svg>
                      Enable Cloud Sync
                    </>
                  )}
                </button>

                {/* Hint text below the button */}
                <p className="text-center text-xs" style={{ color: 'var(--text-secondary)', opacity: 0.55 }}>
                  Uses your device's biometric (Touch ID / Face ID)
                </p>

                {/* Secondary: Sign In (returning user, new device) */}
                <button
                  onClick={loginSync}
                  disabled={isLoading}
                  className="w-full px-4 py-2.5 rounded-xl text-sm font-medium transition-all active:scale-[0.98] disabled:opacity-50"
                  style={{
                    background: 'var(--button-default)',
                    border: '1px solid var(--border-default)',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Already registered? Sign In with Passkey
                </button>
              </div>
            )}
          </section>

          {/* Divider */}
          <div style={{ height: '1px', background: 'var(--border-subtle)' }} />

          {/* Backup & Restore Section */}
          <section>
            <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
              BACKUP &amp; RESTORE
            </h3>
            <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
              Export bookmarks, notes, and settings as JSON, or restore from a previous backup.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleExport}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-[0.98]"
                style={{
                  background: 'var(--gold-muted)',
                  border: '2px solid var(--border-gold)',
                  color: 'var(--gold-hover)',
                }}
              >
                ↓ Export
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-[0.98]"
                style={{
                  background: 'var(--button-default)',
                  border: '2px solid var(--border-strong)',
                  color: 'var(--text-english)',
                }}
              >
                ↑ Import
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json"
              className="hidden"
              onChange={handleImport}
            />
          </section>

          {/* Divider */}
          <div style={{ height: '1px', background: 'var(--border-subtle)' }} />

          {/* Reset Button */}
          <button
            onClick={handleReset}
            className="w-full px-6 py-3 rounded-xl text-sm font-semibold transition-all active:scale-[0.98]"
            style={{
              background: 'var(--button-default)',
              border: '2px solid var(--border-strong)',
              color: 'var(--text-secondary)',
            }}
          >
            Reset All Settings
          </button>
        </div>
      </div>
    </>
  );
};

export default SettingsDrawer;
